import { useEffect, useState } from 'react';

import { ActionText } from '@components/action-text';
import { HideContainer } from '@components/hide-container';
import { IconEmail } from '@components/icons/email';
import { RelativeSpinner } from '@components/loading-spinner';
import { SelectOption } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import {
  FieldMappingEditorValue,
  FieldMappingVariables,
} from '@components/text-editors/addons/field-mapping';
import { EditorValue } from '@components/text-editors/text-editor';
import { useScopedTranslate } from '@locale/translate';

import { createJourneyActionDocumentPath } from 'api/documents/requests';
import { useUploadFile } from 'api/files/hooks';
import { useWebSocket } from 'api/hooks';
import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { TimeSavedData } from 'api/journeys/requests';
import { JourneyActionValuesTypes } from 'api/types';
import { TimeSavedContainer } from 'ui/helpers';
import { useFeature } from 'ui/hooks';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import {
  ActionFooter,
  ActionHeader,
  DescriptionFormField,
  SidebarProps,
  createActionForm,
  useCreateActionFormStore,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import { PreviewEmailProps, usePreviewEmailModal } from './email-preview-modal';

import { FormWithUnsavedChangesPrompt } from '../../journey-builder/unsaved-changes-prompt';
import { TriggerMethod } from '../common';
import { getFieldMappingId } from '../helpers';
import { SendFromEmailSelect } from '../shared/send-from-email-select';

type EmailData = {
  description: string;
  executionMethod: TriggerMethod;
  sendFromEmailId?: string;
  recipients: string;
  cc?: string;
  bcc?: string;
  subject: EditorValue<{ variables?: FieldMappingVariables }>;
  content: EditorValue<{ variables?: FieldMappingVariables }>;
  assignees: SelectOption[];
  attachments: FieldMappingEditorValue[];
} & TimeSavedData;

const Form = createActionForm<EmailData>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { isEnabled: hasContentAIEnabled } = useFeature('FEATURE_AI_CONTENT');
  const { data, isLoading } = useJourneyAction<'send_email'>(journeyActionId);
  const { t } = useScopedTranslate(sidebarLocale);
  const values = data?.values;
  const [ccVisible, setCcVisible] = useState((values?.cc?.length ?? 0) > 0);
  const [bccVisible, setBccVisible] = useState((values?.bcc?.length ?? 0) > 0);
  const formStore = useCreateActionFormStore();
  const isDirty = formStore.useIsDirty();
  const isRequired = formStore.useFieldValue('executionMethod') === 'automatic';
  const formDescription = formStore.useFieldValue('description');
  const formValues = formStore.useFormValues() as JourneyActionValuesTypes['send_email'];
  const fieldMappingId = getFieldMappingId(data?.journey ?? '');
  const baseVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    excludedTypes: ['file', 'file_multi'],
  });
  const emailVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['email'],
  });
  const fileVariableGroups = useVariableGroups({
    actionVariables: data?.actionVariables,
    journeyId: data?.journey ?? '',
    types: ['file', 'file_multi'],
  });
  const showPreviewModal = usePreviewEmailModal();

  useEffect(() => {
    if ((values?.cc?.length ?? 0) > 0) {
      setCcVisible(true);
    }
    if ((values?.bcc?.length ?? 0) > 0) {
      setBccVisible(true);
    }
  }, [values?.cc?.length, values?.bcc?.length]);

  const uploadMutation = useUploadFile(
    createJourneyActionDocumentPath(data?.journey ?? '', journeyActionId),
    false
  );

  const editMutation = useUpdateJourneyAction(journeyActionId);

  const handleSave = (data: EmailData) =>
    editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      action: 'send_email',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        sendFromEmailId: data.sendFromEmailId,
        recipients: data.recipients ?? [],
        cc: data.cc ?? [],
        bcc: data.bcc ?? [],
        subject: data.subject?.value ? data.subject : {},
        content: data.content ?? {},
        attachments: data.attachments ?? [],
      },
      timeSaved: {
        days: data.days,
        hours: data.hours,
        minutes: data.minutes,
      },
    });

  const recipients =
    formValues?.recipients?.map((recipient) => recipient.value) ?? t('noReceipents');
  const sendFromEmailId = formStore.useFieldValue('sendFromEmailId') as string | undefined;

  const emailMessage: PreviewEmailProps = {
    action: 'journey',
    shortId: journeyActionId,
    to: recipients,
    cc: formValues?.cc?.map((cc) => cc.value) ?? [],
    bcc: formValues?.bcc?.map((bcc) => bcc.value) ?? [],
    content: formValues?.content?.value,
    subject: formValues?.subject?.value,
    attachments: (formValues?.attachments ?? []).map((att) =>
      att.variables ? Object.values(att.variables)[0].label : att.value
    ),
    sendFromEmailId,
  };

  const recipientsError = formStore.useFieldError('recipients');
  const subjectError = formStore.useFieldError('subject');
  const contentError = formStore.useFieldError('content');

  const hasEmailContentError =
    recipientsError === 'required' || subjectError === 'required' || contentError === 'required';

  if (isLoading || !data) {
    return <RelativeSpinner />;
  }

  const actionDescription = formDescription || data.description;

  return (
    <Sidebar>
      <ActionHeader title={t('sendEmail')} icon={IconEmail} iconColor="green100" {...props} />
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
          <Stack space={6}>
            <HideContainer title={t('actionDetails')}>
              <DescriptionFormField defaultValue={data.description} />
            </HideContainer>
            <Sidebar.Separator />
            <HideContainer title={t('triggerMethod')}>
              <ExecutionMethod
                assignees={data.assignees}
                isAutomatic={data.isAutomatic}
                formStore={formStore}
                hideLabel
              />
            </HideContainer>
            <Sidebar.Separator />
            <HideContainer title={t('core.email')} error={hasEmailContentError}>
              <Stack space={4}>
                <SendFromEmailSelect
                  Form={Form}
                  defaultValue={values?.sendFromEmailId}
                  isRequired={isRequired}
                />
                <Form.EmailListField
                  id="recipients"
                  label={t('sendTo')}
                  data-cy="email-recipients"
                  defaultValue={values?.recipients}
                  variableGroups={emailVariableGroups}
                  key={`recipients-${String(journeyActionId)}`}
                  validators={isRequired ? ['required'] : []}
                  info={t('sendToInfo')}
                  fieldMappingId={fieldMappingId}
                  labelRightContent={
                    <Stack orientation="horizontal" space={2}>
                      {!ccVisible && (
                        <ActionText
                          font="semibold14"
                          color="textHelper"
                          hoverColor="elementBlue100"
                          underline={false}
                          onClick={(e) => {
                            e.preventDefault();
                            setCcVisible(true);
                          }}
                        >
                          {t('cc')}
                        </ActionText>
                      )}
                      {!bccVisible && (
                        <ActionText
                          font="semibold14"
                          color="textHelper"
                          hoverColor="elementBlue100"
                          underline={false}
                          onClick={(e) => {
                            e.preventDefault();
                            setBccVisible(true);
                          }}
                        >
                          {t('bcc')}
                        </ActionText>
                      )}
                    </Stack>
                  }
                />
                {ccVisible && (
                  <Form.EmailListField
                    id="cc"
                    label={t('cc')}
                    variableGroups={emailVariableGroups}
                    defaultValue={values?.cc}
                    key={`ccRecipients-${String(journeyActionId)}`}
                    fieldMappingId={fieldMappingId}
                  />
                )}
                {bccVisible && (
                  <Form.EmailListField
                    id="bcc"
                    label={t('bcc')}
                    defaultValue={values?.bcc}
                    variableGroups={emailVariableGroups}
                    key={`bccRecipients-${String(journeyActionId)}`}
                    fieldMappingId={fieldMappingId}
                  />
                )}
                <Form.FieldMappingTextEditor
                  id="subject"
                  label={t('subject')}
                  data-cy="email-subject"
                  plaintext
                  singleLine
                  variableGroups={baseVariableGroups}
                  defaultValue={values?.subject}
                  key={`subject-${String(journeyActionId)}`}
                  fieldMappingId={fieldMappingId}
                  validators={isRequired ? ['required'] : []}
                  info={t('subjectInfo')}
                />
                <Form.FieldMappingTextEditor
                  id="content"
                  label={t('content')}
                  data-cy="email-content"
                  variableGroups={baseVariableGroups}
                  defaultValue={values?.content}
                  key={`content-${String(journeyActionId)}`}
                  fieldMappingId={fieldMappingId}
                  validators={isRequired ? ['required'] : []}
                  aiOptions={
                    hasContentAIEnabled
                      ? {
                          generateChoices: actionDescription
                            ? [
                                {
                                  title: t('generateFromActionDescription'),
                                  prompt: `Write an email based on this description: ${actionDescription}`,
                                },
                              ]
                            : [],
                          threadInstructions:
                            'The user is creating email content. You should only write the content not the email subject. Keep the response concise and formatted correctly.',
                          useWebSocket,
                        }
                      : undefined
                  }
                />
                <Form.FieldMappingFileListField
                  id="attachments"
                  label={t('emailAttachment')}
                  defaultValue={values?.attachments}
                  variableGroups={fileVariableGroups}
                  onSelect={uploadMutation.mutateAsync}
                  fieldMappingId={fieldMappingId}
                />
              </Stack>
            </HideContainer>
            <Sidebar.Separator />
            <TimeSavedContainer {...data.timeSaved} />
            <ActionFooter
              action={data}
              onPreview={() => showPreviewModal(emailMessage)}
              isDisabled={!isDirty || editMutation.isLoading}
              form={Form}
            />
          </Stack>
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

const sidebarLocale = {
  en: {
    description: 'Description',
    sendEmail: 'Send email',
    sendFrom: 'Send from',
    addAddress: 'Add email address',
    sendTo: 'Send to',
    subject: 'Email subject',
    subjecPlaceholder: 'Add subject',
    content: 'Message (body)',
    inputPlaceholder: 'Helper text',
    sendToInfo: 'One or more recipient for your email',
    subjectInfo: 'Subject of your email, with optional variables',
    noReceipents: 'No recipients have been selected for the email',
    actionDetails: 'Action Details',
    triggerMethod: 'Trigger Method',
    emailAttachment: 'Email attachment',
    generateFromActionDescription: 'Generate from action description',
    cc: 'CC',
    bcc: 'BCC',
  },
};
